import { Line, LineChart } from 'recharts';
import useCalendarChart, { CalendarChartProps } from './CalendarChart';

export type LinearCalendarChartProps = CalendarChartProps & {
  dayValues: number[];
  reverseColors?: boolean;
  range: number[];
};

const LinearCalendarChart: React.FC<LinearCalendarChartProps> = ({
  dayLabels,
  dayValues,
  reverseColors = false,
  range,
  dayRange,
  periodDots,
  drawHollowPeriodDots = false,
  topLabels,
  showMonthLabels = false,
}) => {
  // Hooks
  const { chartBuildingBlocks, graphHeight, yRange, simpleGraphHeight, topLabelsHeight, domainMaxDay, domainMinDay } =
    useCalendarChart({
      dayLabels,
      dayRange,
      periodDots,
      drawHollowPeriodDots,
      topLabels,
      showMonthLabels,
    });
  const [unsafeRangeMin, unsafeRangeMax] = range;
  const rangeMax = unsafeRangeMax + 0.002;
  const rangeMin = unsafeRangeMin - 0.002;

  const offset = dayLabels[0].diff(domainMinDay, 'days');

  const normalizedValues = dayValues.map((v, i) => {
    if (v === null) return null;
    const normalized = (v - rangeMin) / (rangeMax - rangeMin);
    return {
      value: normalized * 3 + 1.5, // clamping to a 1.5-4.5 range to leave some margin on the graph
      dayIndex: i + 0.5 + offset, // adding .5 to the day index to plot points in the center of day columns
    };
  });

  // for the gradient percentages, we scale the percentage by the proportion of actual graph compared to the total drawing,
  // and take into account the offset from extra elements at the top of the graph
  const gradientStart = (0.8 / yRange) * simpleGraphHeight + topLabelsHeight;
  const gradientEnd = (3.2 / yRange) * simpleGraphHeight + topLabelsHeight;
  // green (top) to red (bottom) gradient
  const gradientName = `higherIsBetter-${rangeMin}-${rangeMax}-${topLabelsHeight}`;
  // green (bottom) to red (top) gradient
  const reverseGradientName = `lowerIsBetter-${rangeMin}-${rangeMax}-${topLabelsHeight}`;

  return (
    <LineChart
      width={(domainMaxDay.diff(domainMinDay, 'days') + 1) * 14}
      height={graphHeight}
      data={normalizedValues}
      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
    >
      {chartBuildingBlocks}
      <defs>
        <linearGradient
          id={gradientName}
          x1="0%"
          x2="0%"
          y1={gradientStart}
          y2={gradientEnd}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#7FBAA9" />
          <stop offset="23%" stopColor="#CBD993" />
          <stop offset="58%" stopColor="#EDC27E" />
          <stop offset="75%" stopColor="#EDA67E" />
          <stop offset="100%" stopColor="#B44359" />
        </linearGradient>
        <linearGradient
          id={reverseGradientName}
          x1="0%"
          x2="0%"
          y1={gradientEnd}
          y2={gradientStart}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#7FBAA9" />
          <stop offset="23%" stopColor="#CBD993" />
          <stop offset="58%" stopColor="#EDC27E" />
          <stop offset="75%" stopColor="#EDA67E" />
          <stop offset="100%" stopColor="#B44359" />
        </linearGradient>
      </defs>
      <Line
        type="monotone"
        dataKey="value"
        animationDuration={100}
        stroke={reverseColors ? `url(#${reverseGradientName})` : `url(#${gradientName})`}
        strokeWidth={4}
        dot={{ strokeWidth: 2 }}
      />
    </LineChart>
  );
};
export default LinearCalendarChart;
